import { polygonColor } from '@utils/map/map.constants';
import { isValidPolygonLocation } from '@utils/validation/coordinates.utils';
import { Feature } from 'geojson';

import { Target } from '@/types/c2/c2.types';
import { FeatureTypeEnum } from '@/types/map.types';
import { PolygonIdentificationEnum } from '@/types/sensor/identification.types';
import { PlotLight, PolygonLocation } from '@/types/sensor/sensor.types';

const POLYGON_TRAJECTORY_SIZE = 10;
const POLYGON_OUTLINE_MIN_OPACITY = 0.35;
const POLYGON_OUTLINE_MAX_OPACITY = 0.75;
const POLYGON_OUTLINE_MIN_WIDTH = 1;
const POLYGON_OUTLINE_MAX_WIDTH = 3;

function getPolygonColor(target: Target<PolygonLocation>): string {
  const identification = target.identification.autoIdentification as unknown as PolygonIdentificationEnum;
  return identification in polygonColor ? polygonColor[identification] : polygonColor.UNKNOWN;
}

function toPolygonRingCoordinates(location: PolygonLocation): number[][] {
  return location.polygon.coordinates[0] ?? [];
}

function getPolygonPlots(target: Target<PolygonLocation>): PlotLight<PolygonLocation>[] {
  const trajectoryPlots = target.trajectory
    .filter((plot) => isValidPolygonLocation(plot.location))
    .slice(-POLYGON_TRAJECTORY_SIZE);

  if (trajectoryPlots.length > 0) {
    return trajectoryPlots;
  }

  if (isValidPolygonLocation(target.lastPosition?.location)) {
    return [target.lastPosition as PlotLight<PolygonLocation>];
  }

  return [];
}

function getOutlineStyle(index: number, totalOutlines: number): { opacity: number; width: number } {
  const ratio = totalOutlines <= 1 ? 1 : (index + 1) / totalOutlines;
  return {
    opacity: POLYGON_OUTLINE_MIN_OPACITY + ratio * (POLYGON_OUTLINE_MAX_OPACITY - POLYGON_OUTLINE_MIN_OPACITY),
    width: POLYGON_OUTLINE_MIN_WIDTH + ratio * (POLYGON_OUTLINE_MAX_WIDTH - POLYGON_OUTLINE_MIN_WIDTH),
  };
}

function toPolygonFillFeature(target: Target<PolygonLocation>, location: PolygonLocation): Feature {
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.POLYGON_LOCATION}-${target.id}`,
    properties: {
      id: target.id,
      featureType: FeatureTypeEnum.POLYGON_LOCATION,
      value: target,
      color: getPolygonColor(target),
      selectOptionLabel: target.displayId ?? target.id,
    },
    geometry: location.polygon,
  } as Feature;
}

function toPolygonLatestOutlineFeature(target: Target<PolygonLocation>, location: PolygonLocation): Feature {
  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.POLYGON_LOCATION_OUTLINE}-${target.id}`,
    properties: {
      id: target.id,
      featureType: FeatureTypeEnum.POLYGON_LOCATION_OUTLINE,
      value: target,
      color: getPolygonColor(target),
      opacity: POLYGON_OUTLINE_MAX_OPACITY,
      width: POLYGON_OUTLINE_MAX_WIDTH,
      selectOptionLabel: target.displayId ?? target.id,
    },
    geometry: {
      type: 'LineString',
      coordinates: toPolygonRingCoordinates(location),
    },
  } as Feature;
}

function toPolygonOutlineFeature(
  target: Target<PolygonLocation>,
  location: PolygonLocation,
  plotIndex: number,
  totalOutlines: number,
): Feature {
  const { opacity, width } = getOutlineStyle(plotIndex, totalOutlines);

  return {
    type: 'Feature',
    id: `${FeatureTypeEnum.POLYGON_TRAJECTORY_OUTLINE}-${target.id}-${plotIndex}`,
    properties: {
      id: target.id,
      featureType: FeatureTypeEnum.POLYGON_TRAJECTORY_OUTLINE,
      color: getPolygonColor(target),
      opacity,
      width,
    },
    geometry: {
      type: 'LineString',
      coordinates: toPolygonRingCoordinates(location),
    },
  } as Feature;
}

export const toPolygonLocationFeatures = (targets: Target<PolygonLocation>[]): Feature[] =>
  targets.flatMap((target) => {
    const plots = getPolygonPlots(target);
    const latestPlot = plots.at(-1);
    if (!latestPlot?.location) {
      return [];
    }

    return [toPolygonFillFeature(target, latestPlot.location)];
  });

export const toPolygonLatestOutlineFeatures = (targets: Target<PolygonLocation>[]): Feature[] =>
  targets.flatMap((target) => {
    const plots = getPolygonPlots(target);
    const latestPlot = plots.at(-1);
    if (!latestPlot?.location) {
      return [];
    }

    return [toPolygonLatestOutlineFeature(target, latestPlot.location)];
  });

export const toPolygonTrajectoryOutlineFeatures = (targets: Target<PolygonLocation>[]): Feature[] =>
  targets.flatMap((target) => {
    const plots = getPolygonPlots(target);
    if (plots.length <= 1) {
      return [];
    }

    const historicalPlots = plots.slice(0, -1);
    return historicalPlots.map((plot, index) =>
      toPolygonOutlineFeature(target, plot.location!, index, historicalPlots.length),
    );
  });
